import _ from 'underscore';
import React, { useCallback, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { EditRegular, ListRegular } from '@fluentui/react-icons';
import { Button } from '@fluentui/react-components';
import { stringifySync } from '@bingads-webui/safe-json';

import { FolderAssetType, ManualAddedImageSource } from '../../../model';
import { GridCellFactory } from './grid-cell-factory';
import { useStyles } from './style';
import { VideoSourceType } from '../../../utils';

export const AdsStudioAssetGridCell = ({
  item,
  assets,
  highlightedText,
  onDetailsClick,
  onEditClick,
  selectable,
  selected,
  onSelect,
  isMultiSelecting,
  i18n,
  logger,
  index,
  draggable,
  onDrag,
  selectedItemCount,
  permissions,
  shouldShowExternalLink,
}) => {
  const classes = useStyles();

  const [focusIndex, setFocusIndex] = useState(-1);
  const focusCellButton = useRef();

  const onKeyDown = useCallback((event, i) => {
    const assetContainers = document.querySelectorAll('.asset-grid-cell-container');
    const firstButton = document.querySelector('.ads-studio-page button');
    switch (event.code) {
      case 'ArrowUp':
      case 'ArrowLeft':
        if (i > 0 && assetContainers[i - 1]) {
          assetContainers[i - 1].focus();
          setFocusIndex(-1);
        }
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        if (i < assetContainers.length - 1 && assetContainers[i + 1]) {
          assetContainers[i + 1].focus();
          setFocusIndex(-1);
        }
        break;
      case 'Space':
        setFocusIndex(i);
        break;
      case 'Tab':
        if (focusIndex < 0 && firstButton) {
          firstButton.focus();
        } else if (assetContainers[i]) {
          const detailButton = assetContainers[i].querySelector('.detail-button');
          const editButton = assetContainers[i].querySelector('.edit-button');
          if (focusCellButton.current === 'edit') {
            if (detailButton) {
              detailButton.focus();
              focusCellButton.current = 'detail';
            }
          } else if (editButton) {
            if (editButton) {
              editButton.focus();
              focusCellButton.current = 'edit';
            }
          } else if (detailButton) {
            detailButton.focus();
            focusCellButton.current = 'detail';
          }
        }
        break;
      case 'Enter':
        if (focusCellButton.current === 'detail') {
          onDetailsClick(item);
        } else if (focusCellButton.current === 'edit') {
          onEditClick(item);
        } else {
          onSelect();
        }
        setFocusIndex(-1);
        break;
      default:
        break;
    }

    event.stopPropagation();
    event.preventDefault();
  }, [focusIndex, item, onDetailsClick, onEditClick, onSelect]);

  const renderOverlayContent = useCallback(() => {
    const clickDetails = (evt) => {
      onDetailsClick(item);
      if (evt) {
        evt.stopPropagation();
        evt.preventDefault();
      }
    };
    const onDetailsKeyDown = (evt) => {
      if (evt.code === 'Enter' || evt.code === 'Space') {
        clickDetails(evt);
      }
    };
    const clickEdit = (evt) => {
      onEditClick(item);
      if (evt) {
        evt.stopPropagation();
        evt.preventDefault();
      }
    };
    const onEditKeyDown = (evt) => {
      if (evt.code === 'Enter' || evt.code === 'Space') {
        clickEdit(evt);
      }
    };
    const { folderAssetType, manualAddedImageSourceType } = item || {};
    const isVideoCell = folderAssetType === FolderAssetType.Video;
    const isStockImageCell = manualAddedImageSourceType === ManualAddedImageSource.ShutterStock;
    const videoTemplateUIEnabled = permissions && permissions.dynamic && permissions.dynamic.VideoTemplateUI;
    const isAIGenerated = item && item.sourceType && item.sourceType === VideoSourceType.AIGenerated;

    const showEditIcon = ((isAIGenerated && videoTemplateUIEnabled && isVideoCell && item && item.id && item.metaData) || !isVideoCell);

    return (
      <React.Fragment>
        { showEditIcon &&
          <Button
            icon={<EditRegular />}
            onClick={clickEdit}
            onKeyDown={onEditKeyDown}
            className={classnames('edit-button', classes.actionButton)}
            title={i18n.getString(_TL_('Edit'))}
            aria-label={i18n.getString(_TL_('Edit asset'))}
          />
        }
        { !isStockImageCell &&
          <Button
            icon={<ListRegular />}
            onClick={clickDetails}
            onKeyDown={onDetailsKeyDown}
            className={classnames('detail-button', classes.actionButton)}
            title={i18n.getString(_TL_('Details'))}
            aria-label={i18n.getString(_TL_('Click to view asset details'))}
          />
        }
      </React.Fragment>
    );
  }, [classes.actionButton, i18n, item, onDetailsClick, onEditClick]);

  useEffect(() => {
    if (logger && logger.logTrace) {
      logger.logTrace(`Grid cell rendered item: ${stringifySync(item)}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={classnames('asset-grid-cell-container', classes.container)}
      onKeyDown={(event) => { onKeyDown(event, index); }}
      style={{ width: '100%', height: '100%' }}
      data-is-focusable
    >
      <GridCellFactory
        item={item}
        assets={assets}
        highlightedText={highlightedText}
        i18n={i18n}
        isOverlayEnabled
        isCellFocused={focusIndex === index}
        renderOverlayContent={renderOverlayContent}
        selectable={selectable}
        selected={selected}
        onClick={onSelect}
        isMultiSelecting={isMultiSelecting}
        onClickVideo={onDetailsClick}
        draggable={draggable}
        onDrag={onDrag}
        selectedItemCount={selectedItemCount}
        logger={logger}
      />
      {shouldShowExternalLink && (
      <div>
        <a href={item.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
          {i18n.getString(_TL_('Preview in browser'))}
        </a>
      </div>
              )}
    </div>
  );
};

AdsStudioAssetGridCell.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
    createdTime: PropTypes.instanceOf(Date),
    // cropping task progress
    updating: PropTypes.bool,
  }).isRequired,
  highlightedText: PropTypes.string,
  onDetailsClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,
  isMultiSelecting: PropTypes.bool,
  index: PropTypes.number,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
    logError: PropTypes.func,
  }),
  draggable: PropTypes.bool,
  onDrag: PropTypes.func,
  selectedItemCount: PropTypes.number,
  shouldShowExternalLink: PropTypes.bool,
};

AdsStudioAssetGridCell.defaultProps = {
  highlightedText: undefined,
  index: -1,
  selectable: false,
  selected: false,
  onSelect: _.noop,
  onEditClick: _.noop,
  isMultiSelecting: false,
  logger: undefined,
  draggable: false,
  onDrag: _.noop,
  selectedItemCount: 1,
  shouldShowExternalLink: false,
};
